import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { AuthService } from "@app/core";
import { retryOnNavigation } from "@app/core/helpers";
import { AccountService } from "@app/core/services/account.service";
import { AppConfigService } from "@app/core/services/app-config.service";
import { ViewAsPartnerService } from "@app/services";
import { SubscriptionManager, UpgradeableItemComponent } from "@app/shared-components";
import { TooltipManagerService, TooltipRegistrationService } from "@app/shared/services";
import { TranslocoService } from "@ngneat/transloco";
import {
  PermissionTypeDto,
  PlatformService,
  PosMarketConnectedCatalogItemDto,
  PosMarketOpenMode,
  PosMarketplaceAvailabilityDto,
  PosMarketplaceService
} from "clearline-api";
import { UserPermissionService } from "clearline-common";
import { RoleTypes } from "core-kit";
import { combineLatest, merge, Observable, of } from "rxjs";
import { catchError, distinctUntilChanged, filter, map, startWith, switchMap } from "rxjs/operators";
import { distinctUntilPlainObjectChanged } from "ui-kit";
import {
  applyConnectedPosItem,
  applyExternalFeatureUrls,
  applyPosMarketplaceAvailability,
  flattenNavMenuItems,
  MenuItemIdentifier,
  NavMenuItem,
  navMenuItemsByRole,
  ParentMenuIdentifier,
  processLockedFeatures,
  SidebarAction,
  SidebarItemOptions,
  updateMenuItemsVisibility
} from "./menus";
import { SidebarActionsService } from "./services/sidebar-actions.service";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent extends SubscriptionManager implements OnInit {
  @ViewChildren("sideNavLink", { read: UpgradeableItemComponent }) sideNavLinkRef: QueryList<UpgradeableItemComponent>;

  @ViewChild("ngxSimplebar") ngxSimplebarRef;

  readonly labelTranslationKey = "common.menu.";

  logoutTooltip = this._userPermissionService.logoutTooltip;
  menuItems: NavMenuItem[] = [];

  private activeBasePath = "";

  constructor(
    private _cdr: ChangeDetectorRef,
    private _router: Router,
    private _configSvc: AppConfigService,
    private _accountSvc: AccountService,
    private _authService: AuthService,
    private _userPermissionService: UserPermissionService,
    private _viewAsPartnerService: ViewAsPartnerService,
    private _sidebarActionsService: SidebarActionsService,
    private _platformService: PlatformService,
    private _posMarketplaceService: PosMarketplaceService,
    private _tooltipRegistration: TooltipRegistrationService,
    private _tooltipManager: TooltipManagerService,
    private _transloco: TranslocoService
  ) {
    super();
  }

  ngOnInit(): void {
    this.activeBasePath = this._router.url;

    this.sub = this._router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(({ urlAfterRedirects }) => {
        this.activeBasePath = urlAfterRedirects;
        this._cdr.markForCheck();
      });

    this.sub = combineLatest([
      this._userPermissionService.userPermission$.pipe(distinctUntilPlainObjectChanged),
      this.externalFeatureUrls$,
      this.posMarketplaceAvailability$,
      this.connectedPosItem$
    ]).subscribe(([userPermission, externalFeatureUrls, posMarketplaceAvailability, connectedPosItem]) => {
      const initialMenuItems = applyConnectedPosItem(applyExternalFeatureUrls(this.roleMenuItems, externalFeatureUrls), connectedPosItem);

      const menuItems = applyPosMarketplaceAvailability(
        processLockedFeatures(initialMenuItems, this._userPermissionService.lockedFeaturesMap),
        posMarketplaceAvailability
      );

      this.menuItems = updateMenuItemsVisibility(menuItems, userPermission);

      this.updateSidebarMenuLabels();
      this.updateTooltipContents(posMarketplaceAvailability);
      this._cdr.markForCheck();
    });
  }

  /**
   * Auto login URLs keyed by external feature code. Only a partner admin has them: the API resolves the
   * URL per partner and lists only platforms the partner holds an account on. Every other role, and any
   * feature the API does not report, keeps the link defined in the menu.
   */
  private get externalFeatureUrls$(): Observable<Map<string, string>> {
    const noExternalFeatureUrls = new Map<string, string>();

    if (!this._accountSvc.isUserInRole(RoleTypes.PartnerAdmin)) {
      return of(noExternalFeatureUrls);
    }

    return this._platformService.getPartnerExternalFeatures().pipe(
      map((externalFeatures) => new Map(externalFeatures.filter(({ url }) => !!url).map(({ code, url }) => [code, url]))),
      catchError(() => of(noExternalFeatureUrls))
    );
  }

  private get roleMenuItems(): NavMenuItem[] {
    return navMenuItemsByRole[this._accountSvc.user?.role] || [];
  }

  private get posMarketplaceAvailability$(): Observable<PosMarketplaceAvailabilityDto | null> {
    const hasPosMarketplaceItem = flattenNavMenuItems(this.roleMenuItems).some(
      ({ identifier }) => identifier === PermissionTypeDto.PosMarketplace
    );

    if (!hasPosMarketplaceItem) {
      return of(null);
    }

    return this.posConnectionChanged$.pipe(
      startWith(undefined),
      switchMap(() => this._posMarketplaceService.getAvailability().pipe(retryOnNavigation(this._router))),
      startWith(null)
    );
  }

  private get posConnectionChanged$(): Observable<void> {
    return merge(this._posMarketplaceService.connectionRecorded$, this._posMarketplaceService.connectionStatusChanged$);
  }

  private get connectedPosItem$(): Observable<PosMarketConnectedCatalogItemDto | null> {
    return this._userPermissionService.userPermission$.pipe(
      map((userPermission) => !!userPermission?.[PermissionTypeDto.PosMarketplace]),
      distinctUntilChanged(),
      switchMap((hasPosMarketplace) =>
        hasPosMarketplace
          ? this.posConnectionChanged$.pipe(
              startWith(undefined),
              switchMap(() =>
                this._posMarketplaceService.getConnectedCatalogItems().pipe(
                  map((items) => (items ?? []).find(({ openMode }) => openMode && openMode !== PosMarketOpenMode.None) ?? null),
                  retryOnNavigation(this._router)
                )
              ),
              startWith(null)
            )
          : of(null)
      )
    );
  }

  trackMenuItem(index: number, item: NavMenuItem): string {
    return `${item.identifier ?? ""}|${item.label ?? index}`;
  }

  /** Check if a parent item should be expanded (has active child route) */
  isParentActive(item: NavMenuItem): boolean {
    if (!item.subItems) return false;
    return this.hasActiveChild(item.subItems);
  }

  /** Toggle logo class on hover — no timeouts, pure event-driven */
  onSidebarHover(enter: boolean): void {
    if (enter) {
      document.body.classList.add("sidebar-logo-expanded");
    }
  }

  onSidebarMouseLeave(event: MouseEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    // If mouse is going to a tooltip, keep sidebar expanded
    const goingToTooltip = relatedTarget?.closest(".tooltip-upgradable-item, ngb-tooltip-window");
    if (goingToTooltip) {
      return;
    }
    // Otherwise close everything
    document.body.classList.remove("sidebar-logo-expanded");
    document.body.classList.remove("sidebar-tooltip-active");
    this._tooltipManager.closeActiveTooltip();
  }

  /** Close any open tooltip when hovering non-tooltip menu items */
  onMenuItemHover(): void {
    this._tooltipManager.closeActiveTooltip();
  }

  onRouterLink(): void {
    this._viewAsPartnerService.deactivateFeature();
    this._tooltipManager.closeActiveTooltip();
  }

  onClick(action: SidebarAction, options: SidebarItemOptions, icon?: string): void {
    this._viewAsPartnerService.deactivateFeature();
    this._sidebarActionsService.executeAction(action, options, icon);
  }

  onUpgradeClick(permission: PermissionTypeDto) {
    const id = this._userPermissionService.lockedFeaturesMap.get(permission)?.id || 0;
    const upgradeUrl = `/manager/pricing-plan?feature=${permission}`;
    this._router.navigateByUrl(`${upgradeUrl}${id > 0 ? `&plan=${id}` : ""}`).then();
  }

  logout() {
    this._authService.logout();
  }

  private hasActiveChild(items: NavMenuItem[]): boolean {
    return items.some((child) => {
      if (child.routePath && this.activeBasePath.startsWith(child.routePath)) {
        return true;
      }
      if (child.subItems) {
        return this.hasActiveChild(child.subItems);
      }
      return false;
    });
  }

  private registerInfoTooltips(): void {
    flattenNavMenuItems(this.menuItems).forEach((item) => {
      if (item.hasInfoTooltip && item.identifier) {
        const tooltipContent = this._transloco.translate(`sidebar.infoTooltip.${item.identifier}`);
        this._tooltipRegistration.registerInfoFeature(item.identifier as PermissionTypeDto, tooltipContent);
      }
    });
  }

  private updateTooltipContents(posMarketplaceAvailability: PosMarketplaceAvailabilityDto | null): void {
    this.registerInfoTooltips();
    this._tooltipRegistration.registerLockedFeature();

    if (posMarketplaceAvailability?.unlock) {
      this._tooltipRegistration.registerPosMarketplaceUnlock(posMarketplaceAvailability.unlock);
    }
  }

  private _setMenuItemLabel(identifier: MenuItemIdentifier, label: string, untranslatedLabel = false) {
    const menuItems = flattenNavMenuItems(this.menuItems);
    const menuItem = menuItems.find((x) => x.identifier === identifier) || null;
    if (menuItem && label) {
      menuItem.label = label;
      menuItem.untranslatedLabel = untranslatedLabel;
    }
  }

  private updateSidebarMenuLabels(): void {
    if (this._userPermissionService.customTransactionsReportName) {
      this._setMenuItemLabel(PermissionTypeDto.CustomTransactionReport, this._userPermissionService.customTransactionsReportName, true);
    }

    this._setMenuItemLabel(
      ParentMenuIdentifier.Sites,
      `${this._configSvc.appData.title} ${this._transloco.translate(`${this.labelTranslationKey}Sites`)}`,
      true
    );
  }
}
